import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import {
  userSendMailChangePass,
  changeUserPassword,
} from "../../actions/userActions";
import {
  USER_SEND_MAIL_CHANGE_PASS_RESET,
  USER_CHANGE_PASS_PROFILE_RESET,
} from "../../constants/userConstants";
import { selectIdentity } from "../../selectors/userSelectors";
import {
  CONFIRM_UNKNOWN_ERROR,
  FIELD_CODE_REQUIRED,
  FIELD_CONFIRM_REQUIRED,
  FIELD_PASSWORD_MISMATCH,
  FIELD_PASSWORD_REQUIRED,
  mapConfirmPasswordError,
  mapSendPasswordCodeError,
} from "./profileChangePasswordFeedback";
import OtpCodeInput, { isCompleteOtpCode } from "../../components/OtpCodeInput";

import "./profileChangePassword.css";

const RESEND_COOLDOWN_SECONDS = 30;
const SUCCESS_TOAST = "Пароль успішно змінено.";

function ProfileChangePasswordScreen() {
  const [changePassCode, setChangePassCode] = useState("");
  const [password, setPassword] = useState("");
  const [passwordRepeat, setPasswordRepeat] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [passwordRepeatVisible, setPasswordRepeatVisible] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [codeSentOnce, setCodeSentOnce] = useState(false);
  const [sendFeedback, setSendFeedback] = useState({ tone: "idle", text: "" });
  const [confirmFeedback, setConfirmFeedback] = useState({
    tone: "idle",
    text: "",
  });
  const [fieldErrors, setFieldErrors] = useState({
    code: "",
    password: "",
    passwordRepeat: "",
  });

  const codeInputRef = useRef(null);
  const passwordInputRef = useRef(null);
  const passwordRepeatInputRef = useRef(null);
  const successHandledRef = useRef(false);
  const [lifecycleReady, setLifecycleReady] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const identity = useSelector(selectIdentity);

  const userSendMailChangePassword = useSelector(
    (state) => state.userSendMailChangePass
  );
  const {
    loading: loadingSendMail,
    error: errorSendMail,
    success: successSendMail,
  } = userSendMailChangePassword || {};

  const changePasswordUser = useSelector((state) => state.changePasswordUser);
  const {
    loading: loadingChangePassword,
    error: errorChangePassword,
    success: successChangePassword,
  } = changePasswordUser || {};

  const identityEmail =
    typeof identity?.email === "string" ? identity.email.trim() : "";

  const canSend =
    Boolean(identityEmail) &&
    resendCooldown === 0 &&
    !loadingSendMail &&
    !loadingChangePassword;

  // Clear stale request/confirm slices on entry and teardown. Gate feedback
  // effects behind lifecycleReady so preloaded success cannot replay toast,
  // cooldown, or navigation on first paint.
  useEffect(() => {
    dispatch({ type: USER_SEND_MAIL_CHANGE_PASS_RESET });
    dispatch({ type: USER_CHANGE_PASS_PROFILE_RESET });
    setLifecycleReady(true);
    return () => {
      dispatch({ type: USER_SEND_MAIL_CHANGE_PASS_RESET });
      dispatch({ type: USER_CHANGE_PASS_PROFILE_RESET });
    };
  }, [dispatch]);

  useEffect(() => {
    if (!identity) {
      navigate("/login", { replace: true });
    }
  }, [identity, navigate]);

  // Send feedback + cooldown. Reset slice after handling so remount/rerender
  // cannot replay success or restart the countdown from stale Redux.
  useEffect(() => {
    if (!lifecycleReady) return;
    if (errorSendMail) {
      setSendFeedback({
        tone: "error",
        text: mapSendPasswordCodeError(errorSendMail),
      });
      dispatch({ type: USER_SEND_MAIL_CHANGE_PASS_RESET });
      return;
    }
    if (successSendMail) {
      setCodeSentOnce(true);
      setSendFeedback({
        tone: "info",
        text: "Код надіслано на вашу пошту.",
      });
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      dispatch({ type: USER_SEND_MAIL_CHANGE_PASS_RESET });
    }
  }, [lifecycleReady, errorSendMail, successSendMail, dispatch]);

  // Confirm feedback + success navigation. Guard against double toast/navigate.
  useEffect(() => {
    if (!lifecycleReady) return;
    if (errorChangePassword) {
      setConfirmFeedback({
        tone: "error",
        text: mapConfirmPasswordError(errorChangePassword),
      });
      dispatch({ type: USER_CHANGE_PASS_PROFILE_RESET });
      return;
    }
    if (!successChangePassword || successHandledRef.current) return;

    successHandledRef.current = true;
    setChangePassCode("");
    setPassword("");
    setPasswordRepeat("");
    setFieldErrors({ code: "", password: "", passwordRepeat: "" });
    setSendFeedback({ tone: "idle", text: "" });
    setConfirmFeedback({ tone: "idle", text: "" });
    dispatch({ type: USER_SEND_MAIL_CHANGE_PASS_RESET });
    dispatch({ type: USER_CHANGE_PASS_PROFILE_RESET });
    toast.success(SUCCESS_TOAST);
    navigate("/profile", { replace: true });
  }, [
    lifecycleReady,
    errorChangePassword,
    successChangePassword,
    dispatch,
    navigate,
  ]);

  // Deterministic 30 → 0 cooldown. Boolean dep keeps one active interval;
  // functional updater avoids stale closures; cleanup clears on unmount/restart.
  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const id = setInterval(() => {
      setResendCooldown((seconds) => (seconds <= 1 ? 0 : seconds - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [resendCooldown > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (loadingSendMail) {
      setSendFeedback({ tone: "info", text: "Надсилаємо код…" });
    }
  }, [loadingSendMail]);

  useEffect(() => {
    if (loadingChangePassword) {
      setConfirmFeedback({ tone: "info", text: "Оновлюємо пароль…" });
    }
  }, [loadingChangePassword]);

  const sendMail = (event) => {
    event.preventDefault();
    if (!canSend) return;
    if (!identityEmail) return;
    setSendFeedback({ tone: "idle", text: "" });
    dispatch(userSendMailChangePass(identityEmail));
  };

  const clearFieldError = (key) => {
    setFieldErrors((prev) =>
      prev[key] ? { ...prev, [key]: "" } : prev
    );
  };

  const validateLocally = () => {
    const nextErrors = {
      code: isCompleteOtpCode(changePassCode) ? "" : FIELD_CODE_REQUIRED,
      password: password ? "" : FIELD_PASSWORD_REQUIRED,
      passwordRepeat: passwordRepeat ? "" : FIELD_CONFIRM_REQUIRED,
    };

    if (password && passwordRepeat && password !== passwordRepeat) {
      nextErrors.passwordRepeat = FIELD_PASSWORD_MISMATCH;
    }

    setFieldErrors(nextErrors);

    if (nextErrors.code) {
      codeInputRef.current?.focus();
      return null;
    }
    if (nextErrors.password) {
      passwordInputRef.current?.focus();
      return null;
    }
    if (nextErrors.passwordRepeat) {
      passwordRepeatInputRef.current?.focus();
      return null;
    }

    return changePassCode;
  };

  const validateHandler = (event) => {
    event.preventDefault();
    if (loadingChangePassword || loadingSendMail) return;
    if (!identityEmail) return;

    const trimmedCode = validateLocally();
    if (trimmedCode == null) return;

    setConfirmFeedback({ tone: "idle", text: "" });
    dispatch(changeUserPassword(identityEmail, trimmedCode, password));
  };

  if (!identity) {
    return null;
  }

  let sendLabel = "Надіслати код";
  if (loadingSendMail) {
    sendLabel = "Надсилання…";
  } else if (resendCooldown > 0 || codeSentOnce) {
    sendLabel = "Надіслати код повторно";
  }

  let confirmLabel = "Оновити пароль";
  if (loadingChangePassword) {
    confirmLabel = "Оновлення…";
  }

  const canConfirmSubmit =
    isCompleteOtpCode(changePassCode) &&
    !loadingChangePassword &&
    !loadingSendMail;

  return (
    <main
      className="add-property-page profile-change-password-page"
      id="Csection"
      tabIndex="-1"
      data-page="change-password"
      data-testid="profile-change-password-page"
    >
      <div className="container profile-change-password-shell">
        <header className="ap-header" data-section="page-header">
          <Link to="/profile" className="cd-back" data-testid="profile-change-password-back">
            <i className="fa-solid fa-arrow-left" aria-hidden="true"></i>
            До профілю
          </Link>
          <div className="ap-header__row">
            <div className="ap-header__text">
              <h1>Змінити пароль</h1>
              <p>
                Код підтвердження буде надіслано на електронну пошту вашого
                акаунта. Введіть код і новий пароль, щоб завершити зміну.
              </p>
            </div>
          </div>
        </header>

        <section
          className="ap-pane profile-change-password-workspace"
          data-testid="profile-change-password-workspace"
          aria-label="Зміна пароля"
        >
          <div
            className="profile-change-password-step"
            data-testid="profile-change-password-step-send"
          >
            <p className="profile-change-password-step__label">Крок 1</p>
            <h2 className="profile-change-password-step__title">
              Отримання коду
            </h2>

            <div
              className="profile-change-password-email"
              data-testid="profile-change-password-email"
            >
              <span className="profile-change-password-email__label">
                Email акаунта
              </span>
              <strong
                className="profile-change-password-email__value"
                data-testid="profile-change-password-email-value"
              >
                {identityEmail || "—"}
              </strong>
            </div>

            <form onSubmit={sendMail} noValidate>
              <div className="profile-change-password-actions">
                <button
                  type="submit"
                  className="action-btn action-btn--accent"
                  disabled={!canSend}
                  data-testid="profile-change-password-send"
                  aria-busy={loadingSendMail ? "true" : "false"}
                >
                  {sendLabel}
                </button>
              </div>
              {resendCooldown > 0 ? (
                <p
                  className="profile-change-password-cooldown"
                  data-testid="profile-change-password-cooldown"
                  role="status"
                  aria-live="polite"
                >
                  Надіслати код повторно можна буде через: {resendCooldown}с
                </p>
              ) : null}
              {sendFeedback.text ? (
                <p
                  className={`profile-change-password-feedback profile-change-password-feedback--${sendFeedback.tone}`}
                  data-testid="profile-change-password-send-feedback"
                  role={sendFeedback.tone === "error" ? "alert" : "status"}
                >
                  {sendFeedback.text}
                </p>
              ) : null}
            </form>
          </div>

          <div
            className="profile-change-password-step"
            data-testid="profile-change-password-step-confirm"
          >
            <p className="profile-change-password-step__label">Крок 2</p>
            <h2 className="profile-change-password-step__title">
              Код і новий пароль
            </h2>

            <form onSubmit={validateHandler} noValidate>
              <div className="ap-row">
                <div className="ap-field ap-field--wide">
                  <label htmlFor="profile-change-password-code">
                    Код підтвердження <span className="req">*</span>
                  </label>
                  <OtpCodeInput
                    id="profile-change-password-code"
                    inputRef={codeInputRef}
                    className={`form-control${fieldErrors.code ? " is-invalid" : ""}`}
                    value={changePassCode}
                    onChange={(nextCode) => {
                      setChangePassCode(nextCode);
                      clearFieldError("code");
                    }}
                    disabled={loadingChangePassword}
                    data-testid="profile-change-password-code"
                    aria-invalid={fieldErrors.code ? "true" : "false"}
                    aria-describedby={
                      fieldErrors.code
                        ? "profile-change-password-code-error"
                        : undefined
                    }
                  />
                  {fieldErrors.code ? (
                    <span
                      id="profile-change-password-code-error"
                      className="profile-change-password-field-error"
                      data-testid="profile-change-password-code-error"
                      role="alert"
                    >
                      {fieldErrors.code}
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="ap-row">
                <div className="ap-field ap-field--wide">
                  <label htmlFor="profile-change-password-new">
                    Новий пароль <span className="req">*</span>
                  </label>
                  <div className="profile-change-password-password">
                    <input
                      id="profile-change-password-new"
                      ref={passwordInputRef}
                      type={passwordVisible ? "text" : "password"}
                      className={`form-control${fieldErrors.password ? " is-invalid" : ""}`}
                      value={password}
                      onChange={(event) => {
                        setPassword(event.target.value);
                        clearFieldError("password");
                      }}
                      autoComplete="new-password"
                      disabled={loadingChangePassword}
                      data-testid="profile-change-password-new"
                      aria-invalid={fieldErrors.password ? "true" : "false"}
                      aria-describedby={
                        fieldErrors.password
                          ? "profile-change-password-new-error profile-change-password-guidance"
                          : "profile-change-password-guidance"
                      }
                    />
                    <button
                      type="button"
                      className="profile-change-password-password__toggle"
                      aria-label={
                        passwordVisible
                          ? "Сховати новий пароль"
                          : "Показати новий пароль"
                      }
                      aria-pressed={passwordVisible ? "true" : "false"}
                      onClick={() => setPasswordVisible((v) => !v)}
                      data-testid="profile-change-password-new-toggle"
                    >
                      <i
                        className={`fa-solid ${passwordVisible ? "fa-eye" : "fa-eye-slash"}`}
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                  {fieldErrors.password ? (
                    <span
                      id="profile-change-password-new-error"
                      className="profile-change-password-field-error"
                      data-testid="profile-change-password-new-error"
                      role="alert"
                    >
                      {fieldErrors.password}
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="ap-row">
                <div className="ap-field ap-field--wide">
                  <label htmlFor="profile-change-password-repeat">
                    Повторіть новий пароль <span className="req">*</span>
                  </label>
                  <div className="profile-change-password-password">
                    <input
                      id="profile-change-password-repeat"
                      ref={passwordRepeatInputRef}
                      type={passwordRepeatVisible ? "text" : "password"}
                      className={`form-control${fieldErrors.passwordRepeat ? " is-invalid" : ""}`}
                      value={passwordRepeat}
                      onChange={(event) => {
                        setPasswordRepeat(event.target.value);
                        clearFieldError("passwordRepeat");
                      }}
                      autoComplete="new-password"
                      disabled={loadingChangePassword}
                      data-testid="profile-change-password-repeat"
                      aria-invalid={
                        fieldErrors.passwordRepeat ? "true" : "false"
                      }
                      aria-describedby={
                        fieldErrors.passwordRepeat
                          ? "profile-change-password-repeat-error"
                          : undefined
                      }
                    />
                    <button
                      type="button"
                      className="profile-change-password-password__toggle"
                      aria-label={
                        passwordRepeatVisible
                          ? "Сховати підтвердження пароля"
                          : "Показати підтвердження пароля"
                      }
                      aria-pressed={passwordRepeatVisible ? "true" : "false"}
                      onClick={() => setPasswordRepeatVisible((v) => !v)}
                      data-testid="profile-change-password-repeat-toggle"
                    >
                      <i
                        className={`fa-solid ${passwordRepeatVisible ? "fa-eye" : "fa-eye-slash"}`}
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                  {fieldErrors.passwordRepeat ? (
                    <span
                      id="profile-change-password-repeat-error"
                      className="profile-change-password-field-error"
                      data-testid="profile-change-password-repeat-error"
                      role="alert"
                    >
                      {fieldErrors.passwordRepeat}
                    </span>
                  ) : null}
                </div>
              </div>

              <ul
                id="profile-change-password-guidance"
                className="profile-change-password-guidance"
                data-testid="profile-change-password-guidance"
              >
                <li>Пароль має містити щонайменше 8 символів.</li>
                <li>Пароль не може складатися лише з цифр.</li>
                <li>Уникайте поширених паролів.</li>
                <li>
                  Уникайте паролів, схожих на ваші особисті дані. Остаточну
                  перевірку виконує сервер.
                </li>
              </ul>

              {confirmFeedback.text ? (
                <p
                  className={`profile-change-password-feedback profile-change-password-feedback--${confirmFeedback.tone}`}
                  data-testid="profile-change-password-confirm-feedback"
                  role={confirmFeedback.tone === "error" ? "alert" : "status"}
                >
                  {confirmFeedback.text}
                </p>
              ) : null}

              <div className="profile-change-password-actions">
                <button
                  type="submit"
                  className="action-btn action-btn--accent"
                  disabled={!canConfirmSubmit}
                  data-testid="profile-change-password-confirm"
                  aria-busy={loadingChangePassword ? "true" : "false"}
                >
                  {confirmLabel}
                </button>
              </div>
            </form>
          </div>
        </section>
      </div>
    </main>
  );
}

export default ProfileChangePasswordScreen;
